import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Paper, Chip, Divider, Stack, CircularProgress
} from '@mui/material';
import { localized, UI, ANSWER_LABELS, QUESTIONS_BY_KEY } from '../../data/questionnaireContent';
import { assessments } from '../../services/api';

const BUCKET_COLOR = { urgent: '#E74C3C', important: '#E67E22', surveiller: '#F1C40F' };
const ANSWER_COLOR = { oui: 'success', non: 'default', je_ne_sais_pas: 'warning' };

function AssessmentReview({ open, onClose, assessmentId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !assessmentId) return;
    setLoading(true);
    assessments.getOne(assessmentId)
      .then((res) => setData(res.data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [open, assessmentId]);

  const answers = data?.answers || {};
  const proposals = data?.proposals || [];
  const answeredKeys = Object.keys(answers);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {localized(UI.panelTitle, lang)}
        {data && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {data.completed_at
              ? new Date(data.completed_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })
              : ''}
            {' · '}
            {data.batiment_id
              ? `${localized(UI.building, lang)} ${data.numero_batiment || ''}`
              : localized(UI.scopeAllSite, lang)}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading || !data ? (
          <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* Propositions */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              {localized(UI.proposalsSection, lang)}
            </Typography>
            {proposals.length === 0 ? (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {localized(UI.noProposalsReview, lang)}
              </Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                {proposals.map((p) => (
                  <Paper key={p.groupKey} variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2, borderLeft: `4px solid ${BUCKET_COLOR[p.bucket] || '#999'}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {t(`thematiques.${p.thematique}`)}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 0.5 }}>
                      {p.item.map((it) => (
                        <Chip key={it} label={t(`riskItems.${it}`)} size="small" variant="outlined" />
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">{p.description}</Typography>
                  </Paper>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Réponses */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              {localized(UI.answersSection, lang)}
            </Typography>
            <Stack spacing={1}>
              {answeredKeys.map((key) => {
                const q = QUESTIONS_BY_KEY[key];
                if (!q) return null;
                return (
                  <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">{localized(q.question, lang)}</Typography>
                    <Chip
                      size="small"
                      label={localized(ANSWER_LABELS[answers[key]], lang)}
                      color={ANSWER_COLOR[answers[key]] || 'default'}
                      sx={{ flexShrink: 0 }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">{localized(UI.close, lang)}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssessmentReview;
